<script lang="ts">import { AsyncLiveWins } from "./api/api";
import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { Api, locale } from "@infinitro/global";
import { Text } from "@infinitro/text";
import { afterUpdate, onDestroy, onMount } from "svelte";
import Swiper from "swiper";
import { Autoplay } from "swiper/modules";
import "swiper/swiper-bundle.css";
import WinCardAlternative from "./parts/win-card-alternative.svelte";
import WinCardAlternative2 from "./parts/win-card-alternative2.svelte";
import WinCardMomentum from "./parts/win-card-momentum.svelte";
import WinCard from "./parts/win-card.svelte";
export let title = undefined;
let unsubscribe;
let lang;
let mounted = false;
let swiper = null;
let swiperContainer;
let wins = [];
let originalWinsCount = 0;
let interval = undefined;
let stripedInverse = false;
let previousLength = 0;
let isLoading = true;
let swiperReady = false;
// iOS Safari visibility handler - restart autoplay when page becomes visible
const handleVisibilityChange = () => {
	if (document.visibilityState === "visible" && swiper?.autoplay) {
		swiper.autoplay.start();
	}
};
const SLIDE_DURATION = 2e3;
const SLIDES_DISPLAYED = 10;
const BUFFER_TIME = 1e4;
const CACHE_DURATION = 4 * 60 * 60 * 1e3;
const CACHE_KEY = "live_wins_cache";
const CACHE_TIMESTAMP_KEY = "live_wins_cache_timestamp";
const POSITION_KEY = "live_wins_position";
// Check if cached data is still valid
const isCacheValid = () => {
	try {
		const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
		if (!timestamp) return false;
		return Date.now() - parseInt(timestamp) < CACHE_DURATION;
	} catch {
		return false;
	}
};
// Load cached data
const loadFromCache = () => {
	try {
		if (!isCacheValid()) return null;
		const cached = localStorage.getItem(CACHE_KEY);
		return cached ? JSON.parse(cached) : null;
	} catch {
		return null;
	}
};
// Save data to cache
const saveToCache = (data) => {
	try {
		localStorage.setItem(CACHE_KEY, JSON.stringify(data));
		localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
		// Reset position when cache refreshes since wins data has changed
		localStorage.removeItem(POSITION_KEY);
	} catch (e) {
		console.warn("Failed to cache live wins", e);
	}
};
// Load last viewed position
const loadPosition = () => {
	try {
		const position = localStorage.getItem(POSITION_KEY);
		return position ? parseInt(position, 10) : 0;
	} catch {
		return 0;
	}
};
// Save current position
const savePosition = (index) => {
	try {
		localStorage.setItem(POSITION_KEY, index.toString());
	} catch {}
};
// Reorder wins to avoid same user appearing consecutively - O(n)
const redistributeWins = (winsArray) => {
	if (winsArray.length <= 1) return winsArray;
	// Group wins by username
	const buckets = new Map();
	for (const win of winsArray) {
		const key = win.username || "anonymous";
		if (!buckets.has(key)) buckets.set(key, []);
		buckets.get(key).push(win);
	}
	// Round-robin pick from buckets, avoiding consecutive same user
	const result = [];
	let lastUsername = null;
	while (result.length < winsArray.length) {
		let picked = false;
		for (const [username, wins] of buckets) {
			if (wins.length > 0 && username !== lastUsername) {
				result.push(wins.shift());
				lastUsername = username;
				picked = true;
				break;
			}
		}
		// If no different user available, take from any non-empty bucket
		if (!picked) {
			for (const [username, wins] of buckets) {
				if (wins.length > 0) {
					result.push(wins.shift());
					lastUsername = username;
					break;
				}
			}
		}
	}
	return result;
};
const initializeSwiper = () => {
	if (swiper) {
		swiper.destroy(true, true);
		swiper = null;
		swiperReady = false;
	}
	if (!swiperContainer) return;
	// Get saved position, ensuring it's within bounds
	const savedPosition = loadPosition();
	const initialSlide = wins.length > 0 ? savedPosition % wins.length : 0;
	swiper = new Swiper(swiperContainer, {
		modules: [Autoplay],
		loop: true,
		loopAddBlankSlides: false,
		slidesPerView: "auto",
		spaceBetween: 16,
		slidesOffsetBefore: 6,
		allowTouchMove: true,
		initialSlide,
		autoplay: {
			delay: SLIDE_DURATION,
			disableOnInteraction: false
		},
		on: {
			afterInit: () => {
				swiperReady = true;
			},
			slideChange: (swiperInstance) => {
				savePosition(swiperInstance.realIndex);
			}
		}
	});
	// Force start autoplay after init
	swiper.autoplay.start();
};
const calculateRefreshInterval = (recordCount) => {
	if (recordCount <= SLIDES_DISPLAYED) {
		return SLIDE_DURATION * 5;
	}
	const totalCycleTime = recordCount * SLIDE_DURATION;
	return Math.max(totalCycleTime - BUFFER_TIME, SLIDE_DURATION * 5);
};
const winKey = (item, index) => {
	return [
		item.playerId || item.username || "anonymous",
		item.providerSlug,
		item.gameSlug,
		item.multiplier,
		item.paidCurrency,
		index
	].join("-");
};
const refreshLiveWins = async (skipCache = false) => {
	try {
		// Try loading from cache first
		if (!skipCache) {
			const cachedWins = loadFromCache();
			if (cachedWins && cachedWins.length > 0) {
				wins = cachedWins;
				originalWinsCount = cachedWins.length;
				isLoading = false;
				// Schedule next refresh when cache expires
				const nextInterval = calculateRefreshInterval(originalWinsCount);
				interval = setTimeout(() => refreshLiveWins(true), nextInterval);
				return;
			}
		}
		// Fetch from API if cache invalid or skipped
		const response = await AsyncLiveWins({
			fetchPolicy: "network-only",
			variables: {},
			context: { api: Api.public }
		});
		if (response.data.liveWins && response.data.liveWins.length > 0) {
			const originalWins = [...response.data.liveWins];
			originalWinsCount = originalWins.length;
			if (originalWinsCount > previousLength) {
				stripedInverse = !stripedInverse;
				previousLength = originalWinsCount;
			}
			// Calculate tier thresholds - using fixed multiplier thresholds
			const tier1Threshold = 150;
			const tier2Threshold = 50;
			const redistributed = redistributeWins(originalWins);
			wins = redistributed.map((win, index) => {
				return {
					...win,
					transparentBackground: stripedInverse ? index % 2 === 1 : index % 2 === 0,
					tier1Threshold,
					tier2Threshold
				};
			});
			// Save to cache
			saveToCache(wins);
			isLoading = false;
		} else {
			// No data returned — mark loading complete so component can hide
			isLoading = false;
		}
	} catch (e) {
		console.warn("Error refreshing live wins", e);
	} finally {
		const nextInterval = calculateRefreshInterval(originalWinsCount);
		interval = setTimeout(() => refreshLiveWins(true), nextInterval);
	}
};
onMount(() => {
	mounted = true;
	// Try loading from cache immediately to avoid skeleton flash
	const cachedWins = loadFromCache();
	if (cachedWins && cachedWins.length > 0) {
		wins = cachedWins;
		originalWinsCount = cachedWins.length;
		isLoading = false;
	}
	if (!interval) interval = setTimeout(refreshLiveWins, 1e3);
	unsubscribe = locale.subscribe(($locale) => {
		lang = $locale;
	});
	document.addEventListener("visibilitychange", handleVisibilityChange);
});
onDestroy(() => {
	unsubscribe();
	document.removeEventListener("visibilitychange", handleVisibilityChange);
	if (swiper) swiper.destroy(true, true);
	clearTimeout(interval);
});
// Initialize swiper after DOM updates when we have data
afterUpdate(() => {
	if (swiperContainer && mounted && wins.length > 0 && !swiper) {
		initializeSwiper();
	}
});
$: usedVerbiage = $stateUi.liveWins.usedVerbiage;
$: useMomentum = $stateUi.liveWins.liveWinsStyle === "momentum";
$: usedVerbiageKey = usedVerbiage;
$: displayTitle = title ?? (usedVerbiage && Verbiage[usedVerbiageKey] in Verbiage ? $t(Verbiage[usedVerbiageKey]) : $t(Verbiage.live_wins));
</script>

{#if mounted && wins.length > 0}
	<div class="section">
		{#if !useMomentum}
			<div class="header-container">
				<div class="title-container">
					<div class="live-dot"></div>
					<div class="title-desktop">
						<Text type="subtitle" subtitleSize="large" subtitleWeight="bold">{displayTitle}</Text>
					</div>
					<div class="title-mobile">
						<Text type="subtitle" subtitleSize="small">{displayTitle}</Text>
					</div>
				</div>
			</div>
		{/if}
		<div class="wins-swiper-container">
			{#if isLoading || wins.length === 0 || !swiperReady}
				<div class="skeleton-container">
					{#each Array(6) as _, index (index)}
						<div class="skeleton-card" class:skeleton-card-momentum={useMomentum}></div>
					{/each}
				</div>
			{/if}
			{#if wins.length > 0}
				<div
					class="swiper live-wins-container"
					class:swiper-hidden={!swiperReady}
					class:live-wins-container-momentum={useMomentum}
					bind:this={swiperContainer}
				>
					<div class="swiper-wrapper">
						{#if $stateUi.liveWins.liveWinsStyle === 'alternative'}
							{#each wins as item, index (winKey(item, index))}
								<div class="swiper-slide">
									<WinCardAlternative liveWin={item} />
								</div>
							{/each}
						{:else if $stateUi.liveWins.liveWinsStyle === 'alternative2'}
							{#each wins as item, index (winKey(item, index))}
								<div class="swiper-slide">
									<WinCardAlternative2 liveWin={item} />
								</div>
							{/each}
						{:else if useMomentum}
							{#each wins as item, index (winKey(item, index))}
								<div class="swiper-slide">
									<WinCardMomentum liveWin={item} />
								</div>
							{/each}
						{:else}
							{#each wins as item, index (winKey(item, index))}
								<div class="swiper-slide">
									<WinCard liveWin={item} />
								</div>
							{/each}
						{/if}
					</div>
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	.section {

		position: relative;

		display: flex;

		width: 100%;

		flex-direction: column;

		overflow: visible;

		font-family: var(--font-main);
}

	.wins-swiper-container {

		position: relative;

		display: flex;

		width: 105%;

		flex-direction: column;

		align-items: center;

		justify-content: center;

		-webkit-mask-image: linear-gradient(
			to right,
			transparent 0.5%,
			black 0%,
			black 90%,
			transparent 99%
		);
		mask-image: linear-gradient(to right, transparent 0.5%, black 0%, black 92%, transparent 98%);
		-webkit-mask-repeat: no-repeat;
		mask-repeat: no-repeat;
}

	@media (min-width: 1024px) {
		.wins-swiper-container {

				width: 100%;
			-webkit-mask-image: linear-gradient(to right, black 0%, black 97%, transparent 99.5%);
			mask-image: linear-gradient(to right, black 0%, black 97%, transparent 99.5%);
			-webkit-mask-repeat: no-repeat;
			mask-repeat: no-repeat;
		}
	}

	.live-wins-container {

		position: relative;

		height: auto;

		width: 100%;

		overflow: hidden;

		padding-top: 0.75rem;

		padding-bottom: 0.75rem;
}

	.live-wins-container.swiper-hidden {

		pointer-events: none;

		position: absolute;

		opacity: 0;
}

	.live-wins-container .swiper-slide {

		display: flex;

		width: -moz-fit-content;

		width: fit-content;

		justify-content: center;

		overflow: visible;

		opacity: 1;
}

	.live-wins-container-momentum {

		background-color: var(--color-background-500);

		padding: 0.5rem;
}
	.header-container {

		display: flex;

		align-items: center;

		justify-content: space-between;
}

	.title-container {

		display: flex;

		align-items: center;
}

	.title-desktop {

		display: none;
}

	@media (min-width: 1024px) {

		.title-desktop {

				display: block;
		}
}

	.title-mobile {

		display: block;
}

	@media (min-width: 1024px) {

		.title-mobile {

				display: none;
		}
}

	.live-dot {

		margin-right: 0.5rem;

		display: inline-block;

		height: 9px;

		width: 9px;

		border-radius: 9999px;
		background-color: var(--color-error-500);
		animation: pulsate 1.5s infinite;
}

	@keyframes pulsate {
		0%,
		100% {
			box-shadow:
				0 0 10px 5px var(--color-error-500),
				0 0 5px 0 var(--color-error-500);
		}
		50% {
			box-shadow:
				0 0 5px 5px color-mix(in srgb, var(--color-error-500), transparent 100%),
				0 0 5px 0 var(--color-error-500);
		}
	}

	.skeleton-container {

		position: relative;

		display: flex;

		width: 100%;

		gap: 1rem;

		overflow: hidden;

		padding-top: 0.75rem;

		padding-bottom: 0.75rem;
		padding-left: 6px;
}

	.skeleton-card {

		flex-shrink: 0;

		border-radius: 0.5rem;
		width: 192px;
		height: 82px;
		background: linear-gradient(
			90deg,
			rgba(255, 255, 255, 0.05) 0%,
			rgba(255, 255, 255, 0.1) 50%,
			rgba(255, 255, 255, 0.05) 100%
		);
		background-size: 200% 100%;
		animation: shimmer 1.5s infinite;
}

	.skeleton-card-momentum {
		width: 258px;
		height: 48px;
	}

	@keyframes shimmer {
		0% {
			background-position: -200% 0;
		}
		100% {
			background-position: 200% 0;
		}
	}
</style>
